/**
 * Main JavaScript file
 * DVT Advies Theme
 */

import Splide from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
        initSmoothScroll();
        initAjaxPagination();
        initPostTypeOverviewImages();
        initHeroAnimation();
        initScrollAnimations();
        initValuesSlider();
        initClientSlider();
    });
}

if (document.readyState !== 'loading') {
    initSmoothScroll();
    initAjaxPagination();
    initPostTypeOverviewImages();
    initHeroAnimation();
    initScrollAnimations();
    initValuesSlider();
    initClientSlider();
}

/**
 * Smooth scroll for anchor links
 */
function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
            const targetId = this.getAttribute('href');
            
            if (targetId === '#') return;
            
            const targetElement = document.querySelector(targetId);
            
            if (targetElement) {
                e.preventDefault();
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        });
    });
}

/**
 * AJAX pagination for post-type overview blocks
 */
function initAjaxPagination() {
    document.querySelectorAll('.js-post-type-overview').forEach(function(section) {
        section.addEventListener('click', function(e) {
            const link = e.target.closest('.c-pagination__item:not(.c-pagination__item--active):not(.c-pagination__item--dots)');
            if (!link) return;

            e.preventDefault();
            loadPage(section, link);
        });
    });
}

/**
 * Mobile expandable images for service post-type overview blocks.
 * Collapsed by default to avoid long scroll; toggle reveals images.
 */
function initPostTypeOverviewImages() {
    document.querySelectorAll('.js-post-type-overview-images').forEach(function(section) {
        const toggle = section.querySelector('.js-post-type-overview-images-toggle');
        if (!toggle) {
            return;
        }

        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = toggle.dataset.labelShow || toggle.textContent;

        toggle.addEventListener('click', function() {
            const isExpanded = section.classList.toggle('is-images-expanded');
            toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

            if (isExpanded) {
                toggle.textContent = toggle.dataset.labelHide || toggle.textContent;
                return;
            }

            toggle.textContent = toggle.dataset.labelShow || toggle.textContent;
        });
    });
}

/**
 * Hero entrance animation
 * Triggers CSS keyframe animations on the hero block's inner elements.
 */
function initHeroAnimation() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const hero = document.querySelector('.c-hero-wrapper');
    if (!hero) return;

    hero.classList.add('is-hero-animated');
}

/**
 * Scroll entrance animations for Gutenberg blocks
 * Fade-up blocks as they enter the viewport, with staggered delays on initial page load.
 */
function initScrollAnimations() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const blocks = document.querySelectorAll('.l-container > *:not(.c-hero-wrapper)');
    if (!blocks.length) return;

    let initialLoad = true;
    let staggerIndex = 0;

    var fallbackTimer = setTimeout(function() {
        blocks.forEach(function(block) {
            if (!block.classList.contains('is-visible')) {
                block.classList.add('is-visible');
            }
        });
    }, 3500);

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;

            if (initialLoad) {
                entry.target.style.transitionDelay = `${staggerIndex * 0.12}s`;
                staggerIndex++;
                entry.target.addEventListener('transitionend', () => {
                    entry.target.style.transitionDelay = '';
                }, { once: true });
            }

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
        });
        initialLoad = false;
    }, { threshold: 0.05, rootMargin: '0px 0px -5% 0px' });

    blocks.forEach(block => {
        block.classList.add('js-scroll-animate');
        observer.observe(block);
    });
}

/**
 * Values Slider (Uitgangspunten)
 * Continuous auto-scrolling carousel using Splide with AutoScroll extension.
 */
function initValuesSlider() {
    document.querySelectorAll('.js-values-slider').forEach(function(el) {
        var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var speed = parseFloat(el.dataset.speed || '0.8');

        if (Number.isNaN(speed)) {
            speed = 0.8;
        }

        new Splide(el, {
            type: 'loop',
            perPage: 4,
            gap: '5.2rem',
            arrows: false,
            pagination: false,
            drag: 'free',
            dragMinThreshold: {
                mouse: 8,
                touch: 16,
            },
            autoWidth: true,
            breakpoints: {
                1024: {
                    perPage: 3,
                    gap: '3.2rem',
                },
                768: {
                    perPage: 2,
                    gap: '3.2rem',
                },
                520: {
                    perPage: 2,
                    gap: '3.2rem',
                },
            },
            autoScroll: {
                speed: reducedMotion ? 0 : speed,
                pauseOnHover: true,
                pauseOnFocus: true,
            },
        }).mount({ AutoScroll: AutoScroll });
    });
}

function initClientSlider() {
    document.querySelectorAll('.js-client-slider').forEach(function(el) {
        var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var speed = parseFloat(el.dataset.speed || '1');

        if (Number.isNaN(speed)) {
            speed = 1;
        }

        new Splide(el, {
            type: 'loop',
            drag: 'free',
            focus: 'center',
            autoWidth: true,
            gap: '12rem',
            breakpoints: {
                640: {
                    gap: '6rem',
                },
            },
            arrows: false,
            pagination: false,
            clones: 10,
            autoScroll: {
                speed: reducedMotion ? 0 : speed,
                pauseOnHover: false,
                pauseOnFocus: false,
            },
        }).mount({ AutoScroll: AutoScroll });
    });
}

function loadPage(section, link) {
    const grid = section.querySelector('.c-post-type-overview__grid');
    const paginationWrap = section.querySelector('.c-post-type-overview__pagination');

    if (!grid) return;

    const href = link.getAttribute('href');
    const pageMatch = href.match(/\/page\/(\d+)/);
    const paramMatch = href.match(/[?&]paged=(\d+)/);
    const page = pageMatch ? parseInt(pageMatch[1], 10)
               : paramMatch ? parseInt(paramMatch[1], 10)
               : 1;

    grid.style.opacity = '0.5';
    grid.style.pointerEvents = 'none';

    const formData = new FormData();
    formData.append('action', 'dvt_post_type_overview');
    formData.append('nonce', dvtAjax.nonce);
    formData.append('post_type', section.dataset.postType);
    formData.append('card_style', section.dataset.cardStyle);
    formData.append('button_text', section.dataset.buttonText || '');
    formData.append('button_icon', section.dataset.buttonIcon || 'none');
    formData.append('posts_per_page', section.dataset.perPage);
    formData.append('orderby', section.dataset.orderby);
    formData.append('order', section.dataset.order);
    formData.append('paged', page);
    formData.append('show_date', section.dataset.showDate || '1');
    formData.append('show_label', section.dataset.showLabel || '1');

    const excludeIds = (section.dataset.exclude || '').split(',').map(function(id) {
        return id.trim();
    }).filter(Boolean);
    excludeIds.forEach(function(id) {
        formData.append('exclude[]', id);
    });

    fetch(dvtAjax.url, {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
    })
    .then(function(response) { return response.json(); })
    .then(function(result) {
        if (!result.success) return;

        grid.innerHTML = result.data.cards;
        grid.style.opacity = '1';
        grid.style.pointerEvents = '';

        if (paginationWrap) {
            paginationWrap.innerHTML = result.data.pagination;
        }

        section.dataset.currentPage = result.data.current;
        section.dataset.totalPages = result.data.total_pages;

        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    })
    .catch(function() {
        grid.style.opacity = '1';
        grid.style.pointerEvents = '';
    });
}

